import { useEffect, useRef, useState } from 'react';
import { apiService } from '../services/api.service';

const COLOR: Record<string, string> = {
  PASS: 'bg-green-500',
  OK: 'bg-green-500',
  FAIL: 'bg-red-500',
  OBSERVATION: 'bg-red-500',
  ERROR: 'bg-yellow-400',
  UNAVAILABLE: 'bg-yellow-400',
  REQUIRES_AUTH: 'bg-yellow-400',
  RATE_LIMITED: 'bg-yellow-400',
  NOT_FOUND: 'bg-orange-400',
  NOT_CONFIGURED: 'bg-gray-400',
  PENDING: 'bg-gray-300',
  MISSING: 'bg-gray-300',
  SKIPPED: 'bg-gray-200',
};

type Board = {
  documentNumber: string;
  providers: Array<{
    id: string;
    code: string;
    name: string;
    group: string;
    isMandatory: boolean;
    blocksOrigination: boolean;
    integrationMode: string;
    adapterConfigured?: boolean;
    latest: {
      id: string;
      status: string;
      outcomeStatus?: string | null;
      summary?: string;
      queriedAt: string;
      durationMs?: number | null;
    } | null;
  }>;
};

export function CustomerValidationsPanel({
  customerId,
  applicationId,
  autoRun = false,
}: {
  customerId?: string;
  applicationId?: string;
  autoRun?: boolean;
}) {
  const [board, setBoard] = useState<Board | null>(null);
  const [loading, setLoading] = useState(false);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [manual, setManual] = useState<{ providerId: string; name: string } | null>(null);
  const [manualStatus, setManualStatus] = useState<'PASS' | 'FAIL'>('PASS');
  const [manualSummary, setManualSummary] = useState('');
  const autoRanFor = useRef<string | null>(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = customerId
        ? await apiService.getCustomerValidations(customerId)
        : await apiService.getApplicationValidations(applicationId!);
      setBoard(data);
    } catch (e: any) {
      setError(e?.response?.data?.message ?? e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (customerId || applicationId) load();
  }, [customerId, applicationId]);

  useEffect(() => {
    const key = customerId || applicationId;
    if (!autoRun || !board || running || !key) return;
    if (autoRanFor.current === key) return;
    const hasAny = board.providers.some((p) => p.latest);
    if (!hasAny) {
      autoRanFor.current = key;
      run();
    }
  }, [autoRun, board, customerId, applicationId, running]);

  const run = async (providerIds?: string[]) => {
    setRunning(true);
    setError(null);
    try {
      if (customerId) await apiService.runCustomerValidations(customerId, providerIds);
      else await apiService.runApplicationValidations(applicationId!, providerIds);
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.message ?? e.message);
    } finally {
      setRunning(false);
    }
  };

  const submitManual = async () => {
    if (!customerId || !manual) return;
    setRunning(true);
    setError(null);
    try {
      await apiService.recordManualValidation(customerId, manual.providerId, {
        status: manualStatus,
        summary: manualSummary,
      });
      setManual(null);
      setManualSummary('');
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.message ?? e.message);
    } finally {
      setRunning(false);
    }
  };

  if (loading && !board) return <p className="text-sm text-gray-500">Cargando validaciones…</p>;

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-600">
          Controles de originación {board?.documentNumber ? `· DNI ${board.documentNumber}` : ''}
        </p>
        <button
          type="button"
          onClick={() => run()}
          disabled={running}
          className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-md disabled:opacity-50"
        >
          {running ? 'Consultando…' : 'Consultar todos'}
        </button>
      </div>
      {error && <div className="p-2 text-sm bg-red-50 border border-red-200 text-red-700 rounded">{error}</div>}
      <div className="bg-white border rounded-lg divide-y">
        {(board?.providers ?? []).map((p) => {
          const status = p.latest?.outcomeStatus || p.latest?.status || 'MISSING';
          return (
            <div key={p.id} className="flex items-center gap-3 px-3 py-2">
              <div className={`w-2.5 h-10 rounded-sm shrink-0 ${COLOR[status] ?? 'bg-gray-300'}`} title={status} />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900">
                  {p.name}
                  {p.isMandatory ? <span className="text-red-600 ml-1">*</span> : null}
                </p>
                <p className="text-xs text-gray-500 truncate">
                  {p.group} · {p.latest?.summary ?? (p.adapterConfigured ? 'Sin consulta' : 'No configurado')}
                  {p.latest?.queriedAt ? ` · ${new Date(p.latest.queriedAt).toLocaleString()}` : ''}
                  {p.latest?.durationMs != null ? ` · ${p.latest.durationMs} ms` : ''}
                </p>
                {p.code === 'AFIP_TRABAJO_BLANCO' || p.code === 'AFIP_MIS_APORTES' || p.code === 'ANSES_CERTIFICADO_NEGATIVA' || p.code === 'ANSES_OBRA_SOCIAL' ? (
                  <p className="text-xs text-amber-800 mt-1">
                    {p.code === 'ANSES_OBRA_SOCIAL'
                      ? 'CODEM no tiene API REST. El titular consulta DNI/CUIL en ANSES (reCAPTCHA). No se simula obra social ni se decide APROBADO/RECHAZADO aquí.'
                      : p.code === 'ANSES_CERTIFICADO_NEGATIVA'
                      ? 'ANSES no publica API REST de Certificación Negativa. El titular consulta en anses.gob.ar (período: últimos 6 meses) o mi ANSES. No se simula el certificado ni se scrapea el WAF.'
                      : p.code === 'AFIP_MIS_APORTES'
                      ? 'Aportes en Línea no tiene API para originación. El titular consulta en ARCA (Clave Fiscal) o en la consulta pública con reCAPTCHA. No se simulan períodos ni pagos.'
                      : 'ARCA no ofrece API para originación. El titular consulta en ARCA (Clave Fiscal) o en la consulta pública con CAPTCHA. Acá no se simula “en blanco” ni se scrapea.'}
                    {p.integrationMode === 'MANUAL' ? ' Use carga manual con lo que el titular obtuvo en el organismo.' : ' Puede pasar el control a modo Manual en Ajustes.'}
                  </p>
                ) : null}
              </div>
              <div className="flex gap-2 shrink-0">
                {p.integrationMode !== 'MANUAL' && (
                  <button type="button" onClick={() => run([p.id])} className="text-xs text-blue-600 hover:underline">
                    Consultar
                  </button>
                )}
                {p.integrationMode === 'MANUAL' && customerId && (
                  <button
                    type="button"
                    onClick={() => setManual({ providerId: p.id, name: p.name })}
                    className="text-xs text-blue-600 hover:underline"
                  >
                    Carga manual
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
      <p className="text-xs text-gray-400">
      * Obligatorio. Gris: sin consulta o no configurado · Verde: coincide con ficha / OK · Rojo: observación o no coincide · Amarillo: error / no disponible · Nunca se simula un OK
      </p>

      {manual && (
        <div className="border rounded-lg p-3 bg-gray-50 space-y-2">
          <p className="text-sm font-medium">Carga manual — {manual.name}</p>
          <select
            value={manualStatus}
            onChange={(e) => setManualStatus(e.target.value as 'PASS' | 'FAIL')}
            className="px-2 py-1 border rounded text-sm"
          >
            <option value="PASS">Verde (OK)</option>
            <option value="FAIL">Rojo (observación)</option>
          </select>
          <input
            value={manualSummary}
            onChange={(e) => setManualSummary(e.target.value)}
            placeholder="Nota / resultado"
            className="w-full px-2 py-1 border rounded text-sm"
          />
          <div className="flex gap-2">
            <button type="button" onClick={submitManual} className="px-3 py-1 bg-blue-600 text-white rounded text-sm">
              Guardar
            </button>
            <button type="button" onClick={() => setManual(null)} className="px-3 py-1 border rounded text-sm">
              Cancelar
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
